import { IbdRecommendationTile } from '@hub/free-form-hub/common/recommendations/ibd-recommendation/IbdRecommendationTile';
import { IgeRecommendationTile } from '@hub/free-form-hub/common/recommendations/ige-recommendation/igeRecommendationTile';
import React from 'react';
import type { IbdRecommendation } from '@hub/free-form-hub/common/recommendations/ibd-recommendation/useIbdRecommendation';
import type { IgeRecommendationResults } from '@hub/free-form-hub/common/recommendations/ige-recommendation/useIgeRecommendation';
import type { Session } from '@hub/hooks/states/useSessionStore';
import { APP_NAME, EnvironmentName } from '@hub/libs/constants';
import { useTranslation } from 'next-i18next';
import getConfig from 'next/config';
import type { NextRuntimeConfiguration } from '@hub/libs/config';
import { ffhEventCategory, TrackedFfhEventActions } from '@hub/free-form-hub/tracking.config';
import { usePiwikPro } from '@piwikpro/next-piwik-pro';

const { publicRuntimeConfig } = getConfig() as NextRuntimeConfiguration;

type NbsRecommendationProps = {
  ibdRecommendation: IbdRecommendation;
  igeRecommendation: IgeRecommendationResults;
  quantity: number;
  session: Session;
  fileDimensions: Record<string, number>;
};

type BaseLinks = { cnc: string; injectionMolding: string };

const buildUtmLink = (base: string, campaign: string): string => {
  let url: URL;
  url = new URL(base);
  if (typeof window !== 'undefined') {
    url = new URL(base, window.location.origin);
  } else {
    return base;
  }

  const params = new URLSearchParams(url.search);
  params.set('utm_source', 'igusdeshop');
  params.set('utm_media', 'cadiframeclick');
  params.set('utm_campaign', campaign);
  url.search = params.toString();
  return url.toString();
};

export const NbsRecommendation = ({
  ibdRecommendation,
  igeRecommendation,
  quantity,
  session,
  fileDimensions,
}: NbsRecommendationProps) => {
  const { t } = useTranslation(['free-form-hub/freeFormHubUpload']);
  const env = publicRuntimeConfig.envName as EnvironmentName;
  const { CustomEvent } = usePiwikPro();

  const baseLinksByEnv: Partial<Record<EnvironmentName, BaseLinks>> = {
    [EnvironmentName.ORANGE]: {
      cnc: 'https://iglidur-cnc-designer.igus.tools/',
      injectionMolding: 'https://injection-molding-designer.igus.tools/',
    },
    [EnvironmentName.WHITE]: {
      cnc: 'https://icn-kopla-integration.igusdev.igus.de',
      injectionMolding: 'https://imd-kopla-integration.igusdev.igus.de',
    },
  } as const;
  const selectedBases = baseLinksByEnv[env];

  const links = {
    cnc: buildUtmLink(selectedBases.cnc, 'hmishopclick_cnc'),
    injectionMolding: buildUtmLink(selectedBases.injectionMolding, 'hmishopclick_form'),
  };

  return (
    <div>
      {!!ibdRecommendation?.data && (
        <IbdRecommendationTile
          data={ibdRecommendation?.data}
          fileDimensions={fileDimensions}
          quantity={quantity}
          session={session}
        />
      )}
      {!!igeRecommendation && (
        <IgeRecommendationTile igeResult={igeRecommendation} fileDimensions={fileDimensions} session={session} />
      )}
      <div className="flex flex-col mt-10 w-full md:w-[540px] m-auto">
        <div className="text-xl text-center my-4">
          {t('cus.other-options.HINT', { ns: 'free-form-hub/freeFormHubUpload' })}
          <br />
          {t('cus.other-options.HINT_SUBTITLE', { ns: 'free-form-hub/freeFormHubUpload' })}
        </div>
        <div className="flex flex-col md:flex-row gap-6">
          <a
            id="cad-search-cnc-link"
            href={links.cnc}
            className="flex-1 min-w-0 flex flex-col items-center justify-center px-3 no-underline hover:underline hover:opacity-80 mb-3 md:m-0"
            target="_blank"
            rel="noreferrer"
            onClick={() => CustomEvent.trackEvent(ffhEventCategory, TrackedFfhEventActions.goToIcn, 'origin=homepage')}
          >
            <figure className="h-[60px] w-[75px] flex-shrink-0">
              <img
                src={`/_${APP_NAME}/images/ffh/icon-cnc.svg`}
                alt="CNC machined parts"
                className="block h-[60px] w-auto object-contain"
              />
            </figure>
            <p className="text-[#e77600] text-xl text-center font-bold my-2">
              {t('cus.other-options.CNC', { ns: 'free-form-hub/freeFormHubUpload' })}
            </p>
            <p className="text-[#413e46] text-xs font-light text-center break-words">
              {t('cus.other-options.CNC_DESCRIPTION', { ns: 'free-form-hub/freeFormHubUpload' })}
            </p>
          </a>

          <a
            href={links.injectionMolding}
            className="flex-1 md:max-w-[260px] flex flex-col items-center justify-start px-3 hover:underline hover:opacity-80"
            target="_blank"
            id="cad-search-form-link"
            rel="noreferrer"
            onClick={() => CustomEvent.trackEvent(ffhEventCategory, TrackedFfhEventActions.goToImd, 'origin=homepage')}
          >
            <figure className="h-[60px] w-[75px] flex-shrink-0">
              <img
                src={`/_${APP_NAME}/images/ffh/icon-injection-molding.svg`}
                alt="Injection molding"
                className="block h-[60px] w-auto object-contain"
              />
            </figure>
            <p className="text-[#e77600] text-xl text-center font-bold my-2">
              {t('cus.other-options.IM', { ns: 'free-form-hub/freeFormHubUpload' })}
            </p>
            <p className="text-[#413e46] text-xs font-light text-center break-words">
              {t('cus.other-options.IM_DESCRIPTION', { ns: 'free-form-hub/freeFormHubUpload' })}
            </p>
          </a>
        </div>
      </div>
    </div>
  );
};
